import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ModalFormWrapper } from '@/components/ui/ModalFormWrapper';
import { ClinicCreate, ClinicCreateSchema } from '@/schema/clinic';
import { useCreateClinic } from '@/hooks/useClinic';
import { useQueryClient } from '@tanstack/react-query';
import { InviteDialog } from './InviteDialog';

/**
 * Two steps: create the clinic, then invite its first orthodontist. A clinic with nobody
 * in it is not useful, so the invite is offered immediately instead of being a separate
 * errand the admin has to remember.
 */
export function NewClinicDialog({ onClose }: { onClose: () => void }) {
  const [createdClinicId, setCreatedClinicId] = useState<string | null>(null);
  const createClinic = useCreateClinic();
  const queryClient = useQueryClient();

  const form = useForm<ClinicCreate>({
    resolver: zodResolver(ClinicCreateSchema),
    defaultValues: { name: '', address: '', email: '', phone: '' },
  });

  const handleSubmit = (data: ClinicCreate) => {
    createClinic.mutate(data, {
      onSuccess: (clinicId) => {
        queryClient.invalidateQueries({ queryKey: ['admin', 'clinics'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
        toast.success('Clinic created');
        setCreatedClinicId(String(clinicId));
      },
      onError: () => toast.error('Could not create the clinic'),
    });
  };

  if (createdClinicId) {
    return <InviteDialog defaultClinicId={createdClinicId} onClose={onClose} />;
  }

  return (
    <ModalFormWrapper
      form={form}
      submitText={createClinic.isPending ? 'Creating…' : 'Create clinic'}
      submitButton="default"
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <Form {...form}>
        <div className="flex flex-col gap-5 text-white">
          <header className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">New clinic</p>
            <p className="text-sm text-white/55">You can invite its first orthodontist next.</p>
          </header>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name *</FormLabel>
                <FormControl>
                  <Input type="text" {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address</FormLabel>
                <FormControl>
                  <Input type="text" {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input type="tel" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </Form>
    </ModalFormWrapper>
  );
}
